import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmojiPicker from "emoji-picker-react";
import { Loader2, Plus, Save, Share2, Trash2, Eye, Pencil, ChevronRight, GripVertical, Copy, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import {
  createRevisionSnapshot,
  deleteRevision,
  deleteRevisionTimelineEvent,
  getRevision,
  getRevisionTimeline,
  getUsers,
  shareRevision,
  shareRevisionTimelineRespuesta,
  updateRevision,
} from "@/lib/api";
import RevisionReplyAdjuntos from "@/components/RevisionReplyAdjuntos";
import { revisionBlockPreview } from "@/components/RevisionBloquesPreview";
import { useUnreadRevisiones } from "@/contexts/UnreadRevisionesContext";

const EMOJI_PREDETERMINADO = "📝";
const BLOCK_TYPES = [
  { value: "titulo", label: "Título (H1–H6)" },
  { value: "parrafo", label: "Párrafo" },
  { value: "carousel", label: "Carousel" },
  { value: "enlaces_recursos", label: "Enlaces y recursos" },
].sort((a, b) => a.label.localeCompare(b.label, "es"));

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/")) return reject(new Error("Solo imágenes"));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });

const readFileAsDataUrlAny = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return reject(new Error("Sin archivo"));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });

const createBlock = (tipo) => {
  const id = `blk_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  if (tipo === "titulo") return { id, tipo, nivel: 1, texto: "" };
  if (tipo === "parrafo") return { id, tipo, texto: "" };
  if (tipo === "input") return { id, tipo, titulo: "", placeholder: "" };
  if (tipo === "area_texto") return { id, tipo, titulo: "", placeholder: "" };
  if (tipo === "archivo") return { id, tipo, titulo: "" };
  if (tipo === "carousel") return { id, tipo, imagenes: [] };
  if (tipo === "enlaces_recursos") return { id, tipo, items: [{ tipo: "url", url: "", texto: "" }] };
  return { id, tipo, texto: "" };
};

const linkifyText = (text) => {
  const str = String(text || "");
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const isUrlLike = (part) => /^https?:\/\/\S+$/i.test(part) || /^www\.\S+$/i.test(part);
  return str.split(urlRegex).map((part, idx) =>
    isUrlLike(part) ? (
      <a key={idx} href={part.startsWith("http") ? part : `https://${part}`} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">
        {part}
      </a>
    ) : (
      <span key={idx}>{part}</span>
    )
  );
};

function RevisionBlockRow({
  block,
  index,
  allBlocks,
  onUpdate,
  onRemove,
  onDuplicate,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
  addWidgetMenuOptions = null,
  onAddWidgetAfter = null,
}) {
  const b = block;
  const update = (key, value) => onUpdate(index, { ...block, [key]: value });
  const updateNested = (field, i, val) => {
    const arr = [...(block[field] || [])];
    arr[i] = val;
    onUpdate(index, { ...block, [field]: arr });
  };
  const removeItem = (field, i) => {
    const arr = (block[field] || []).filter((_, j) => j !== i);
    onUpdate(index, { ...block, [field]: arr });
  };
  const addItem = (field, item) => {
    onUpdate(index, { ...block, [field]: [...(block[field] || []), item] });
  };
  const showAddAfter =
    typeof onAddWidgetAfter === "function" && Array.isArray(addWidgetMenuOptions) && addWidgetMenuOptions.length > 0;
  return (
    <div
      className={`group/rev-block relative rounded-lg border border-gray-300 bg-gray-50 ${isDragging ? "opacity-50" : ""}`}
      draggable
      data-block-index={index}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex gap-2 p-3">
      <div className="cursor-grab active:cursor-grabbing shrink-0 self-center text-muted-foreground hover:text-foreground touch-none" aria-label="Reordenar">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide m-0">
          {index + 1}. {BLOCK_TYPES.find((t) => t.value === block.tipo)?.label ?? block.tipo}
        </h5>
        <div className="space-y-2">
          {block.tipo === "titulo" && (
            <>
              <div className="flex gap-2 items-center">
                <Label className="shrink-0 text-xs">Nivel</Label>
                <Select
                  value={String(block.nivel ?? 1)}
                  onValueChange={(v) => update("nivel", parseInt(v, 10))}
                >
                  <SelectTrigger className="w-20 h-9 bg-white border-gray-300 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <SelectItem key={n} value={String(n)}>H{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input className="bg-white border-gray-300" value={block.texto || ""} onChange={(e) => update("texto", e.target.value)} placeholder="Texto" />
            </>
          )}
          {block.tipo === "parrafo" && <Input className="bg-white border-gray-300" value={block.texto || ""} onChange={(e) => update("texto", e.target.value)} placeholder="Texto" />}
          {block.tipo === "input" && (
            <>
              <Input className="bg-white border-gray-300" value={block.titulo || ""} onChange={(e) => update("titulo", e.target.value)} placeholder="Etiqueta" />
              <Input className="bg-white border-gray-300" value={block.placeholder || ""} onChange={(e) => update("placeholder", e.target.value)} placeholder="Placeholder" />
            </>
          )}
          {block.tipo === "area_texto" && <Input className="bg-white border-gray-300" value={block.titulo || ""} onChange={(e) => update("titulo", e.target.value)} placeholder="Título" />}
          {block.tipo === "archivo" && <Input className="bg-white border-gray-300" value={block.titulo || ""} onChange={(e) => update("titulo", e.target.value)} placeholder="Título del campo archivo" />}
          {block.tipo === "carousel" && (
            <div className="space-y-2">
              <Label>Imágenes (arrastra o selecciona)</Label>
              <div className="flex flex-wrap gap-2">
                {(b.imagenes || []).map((img, i) => (
                  <div key={i} className="relative group">
                    {img?.url ? (
                      <img src={img.url} alt="" className="h-20 w-20 object-cover rounded border" />
                    ) : (
                      <div className="h-20 w-20 rounded border border-dashed bg-muted/30 flex items-center justify-center text-muted-foreground text-xs">?</div>
                    )}
                    <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 h-6 w-6 opacity-90 group-hover:opacity-100" onClick={() => removeItem("imagenes", i)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div
                className="border border-dashed border-gray-300 rounded-lg p-4 text-center text-sm text-muted-foreground bg-white hover:bg-gray-50 cursor-pointer"
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-gray-100"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("bg-gray-100"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("bg-gray-100");
                  const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
                  Promise.all(files.map((file) => readFileAsDataUrl(file)))
                    .then((urls) => {
                      const newItems = urls.map((url) => ({ url }));
                      onUpdate(index, { ...b, imagenes: [...(b.imagenes || []), ...newItems] });
                    })
                    .catch(() => {});
                }}
                onClick={() => document.getElementById(`revision-carousel-file-${b.id}`)?.click()}
              >
                Arrastra imágenes aquí o haz clic para elegir
              </div>
              <input
                id={`revision-carousel-file-${b.id}`}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  Promise.all(files.map((file) => readFileAsDataUrl(file)))
                    .then((urls) => {
                      const newItems = urls.map((url) => ({ url }));
                      onUpdate(index, { ...b, imagenes: [...(b.imagenes || []), ...newItems] });
                    })
                    .catch(() => {});
                  e.target.value = "";
                }}
              />
            </div>
          )}
          {block.tipo === "enlaces_recursos" && (
            <div className="space-y-2">
              <Label>Items (URLs o archivos para que el cliente descargue)</Label>
              {(b.items || []).map((item, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center rounded border border-gray-300 bg-white p-2 w-full">
                  <Select value={item.tipo || "url"} onValueChange={(v) => updateNested("items", i, { ...item, tipo: v })}>
                    <SelectTrigger className="w-28 bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="url">URL</SelectItem>
                      <SelectItem value="archivo">Archivo</SelectItem>
                    </SelectContent>
                  </Select>
                  {(item.tipo || "url") === "url" && (
                    <>
                      <Input
                        className="flex-1 min-w-[120px] bg-white border-gray-300"
                        placeholder="URL"
                        value={item.url ?? ""}
                        onChange={(e) => updateNested("items", i, { ...item, url: e.target.value })}
                      />
                      <Input
                        className="flex-1 min-w-[120px] bg-white border-gray-300"
                        placeholder="Texto"
                        value={item.texto ?? ""}
                        onChange={(e) => updateNested("items", i, { ...item, texto: e.target.value })}
                      />
                    </>
                  )}
                  {(item.tipo || "url") === "archivo" && (
                    <>
                      <input
                        id={`revision-enlaces-recursos-archivo-${b.id}-${i}`}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = (e.target.files || [])[0];
                          if (file) {
                            readFileAsDataUrlAny(file).then((url) => {
                              updateNested("items", i, { ...item, url, nombre_archivo: file.name });
                            }).catch(() => {});
                          }
                          e.target.value = "";
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`revision-enlaces-recursos-archivo-${b.id}-${i}`)?.click()}
                      >
                        {item.nombre_archivo || "Seleccionar archivo"}
                      </Button>
                      {item.url && (
                        <span className="text-xs text-muted-foreground truncate max-w-[140px]" title={item.nombre_archivo}>{item.nombre_archivo}</span>
                      )}
                    </>
                  )}
                  <div className="ml-auto shrink-0">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem("items", i)} aria-label="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => addItem("items", { tipo: "url", url: "", texto: "" })}>
                <Plus className="w-4 h-4 mr-1" /> Añadir item
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" aria-label="Mover bloque">
                <ArrowRightLeft className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[280px] overflow-y-auto">
              {allBlocks.map((b, i) => (
                <DropdownMenuItem key={b.id || i} onSelect={() => onMove(index, i + 1)} disabled={i === index}>
                  {i + 1}. {b.tipo || "bloque"}{i === index ? " (actual)" : ""}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button type="button" variant="ghost" size="icon" onClick={() => onDuplicate(index)} aria-label="Duplicar bloque">
            <Copy className="w-4 h-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => onRemove(index)} aria-label="Eliminar bloque">
            <Trash2 className="w-4 h-4" />
          </Button>
      </div>
      </div>
      {showAddAfter ? (
        <div className="flex justify-center border-t border-transparent px-3 py-1.5 transition-colors group-hover/rev-block:border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs shadow-sm bg-background opacity-0 pointer-events-none transition-opacity group-hover/rev-block:opacity-100 group-hover/rev-block:pointer-events-auto"
                aria-label="Añadir widget siguiente"
              >
                <Plus className="w-3.5 h-3.5" />
                Añadir siguiente
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="max-h-[280px] overflow-y-auto">
              {addWidgetMenuOptions.map((opt) => (
                <DropdownMenuItem key={opt.value} onSelect={() => onAddWidgetAfter(opt.value)}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  );
}

export default function RevisionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbeddedInRevisiones = (location.pathname || "").startsWith("/revisiones/");
  const outletContext = useOutletContext();
  const setRevisionPreview = outletContext?.setRevisionPreview;
  const removeRevisionFromSidebarList = outletContext?.removeRevisionFromSidebarList;
  const {
    unreadByRevision,
    readTimelineIdsByRevision,
    setBulkUnreadByRevision,
    setRevisionTimelineEventRead,
    isRevisionTimelineEventRead,
  } = useUnreadRevisiones();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  const [activeTab, setActiveTab] = useState("revisiones");
  const [modoVisualizar, setModoVisualizar] = useState(false);
  const [expandedTimelineId, setExpandedTimelineId] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [activeSnapshotId, setActiveSnapshotId] = useState("");
  const [sharingGeneric, setSharingGeneric] = useState(false);
  const [sharingTimelineEventId, setSharingTimelineEventId] = useState(null);
  const [deletingTimelineEventId, setDeletingTimelineEventId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const [titulo, setTitulo] = useState("");
  const [slug, setSlug] = useState("");
  const [emoji, setEmoji] = useState("");
  const [imagenCabecera, setImagenCabecera] = useState("");
  const [imagenFondo, setImagenFondo] = useState("");
  const [notificacionEmails, setNotificacionEmails] = useState([]);
  const [usuariosPlataforma, setUsuariosPlataforma] = useState([]);

  const [draftBlocks, setDraftBlocks] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const fondoInputRef = useRef(null);
  const cabeceraInputRef = useRef(null);

  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [revision, timelineData] = await Promise.all([getRevision(id), getRevisionTimeline(id)]);
      setTitulo(revision.titulo || "");
      setSlug(revision.slug || "");
      setEmoji(revision.emoji || "");
      setImagenCabecera(revision.imagen_cabecera || "");
      setImagenFondo(revision.imagen_fondo || "");
      setNotificacionEmails(Array.isArray(revision.notificacion_emails) ? revision.notificacion_emails : []);
      setDraftBlocks(Array.isArray(revision.bloques) ? revision.bloques : []);
      const snaps = Array.isArray(revision.snapshots) ? revision.snapshots : [];
      setSnapshots(snaps);
      setActiveSnapshotId((prev) => prev || snaps[0]?.id || "");
      const allTimeline = timelineData.timeline || [];
      setTimeline(allTimeline);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar la hoja");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const clientEvents = timeline.filter((t) => t.tipo === "respuesta_cliente");
    const read = readTimelineIdsByRevision[id] || {};
    const unreadCount = clientEvents.filter((e) => !read[e.id]).length;
    setBulkUnreadByRevision({ [id]: unreadCount });
  }, [id, timeline, readTimelineIdsByRevision, setBulkUnreadByRevision]);

  useEffect(() => {
    getUsers()
      .then((data) => setUsuariosPlataforma(Array.isArray(data.users) ? data.users : []))
      .catch(() => setUsuariosPlataforma([]));
  }, []);

  useEffect(() => {
    if (!id || !setRevisionPreview) return;
    setRevisionPreview(id, { emoji, titulo, slug });
    return () => setRevisionPreview(id, null);
  }, [id, emoji, titulo, slug, setRevisionPreview]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await updateRevision(id, {
        titulo: titulo.trim(),
        slug: slug.trim(),
        emoji: (emoji || "").trim(),
        imagen_cabecera: imagenCabecera,
        imagen_fondo: imagenFondo,
        notificacion_emails: notificacionEmails,
        bloques: draftBlocks,
      });
      toast.success("Guardado");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSnapshot = async () => {
    if (!id) return;
    setCreatingSnapshot(true);
    try {
      await createRevisionSnapshot(id, { titulo, bloques: draftBlocks });
      toast.success("Revisión añadida");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear la revisión");
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm("¿Eliminar esta hoja de trabajo?")) return;
    try {
      await deleteRevision(id);
      removeRevisionFromSidebarList?.(id);
      toast.success("Hoja eliminada");
      navigate("/revisiones");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const handleShareRevision = async () => {
    if (!id || sharingGeneric) return;
    setSharingGeneric(true);
    try {
      const data = await shareRevision(id);
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Enlace copiado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo compartir");
    } finally {
      setSharingGeneric(false);
    }
  };

  const handleDeleteTimelineEvent = async (e, eventId) => {
    e.stopPropagation();
    if (!id || !eventId || deletingTimelineEventId) return;
    if (!window.confirm("¿Eliminar esta respuesta? Esta acción no se puede deshacer.")) return;
    setDeletingTimelineEventId(eventId);
    try {
      await deleteRevisionTimelineEvent(id, eventId);
      setTimeline((prev) => prev.filter((t) => t.id !== eventId));
      setExpandedTimelineId((prev) => (prev === eventId ? null : prev));
      toast.success("Respuesta eliminada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setDeletingTimelineEventId(null);
    }
  };

  const handleShareRevisionTimelineReply = async (e, eventId) => {
    e.stopPropagation();
    if (!id || !eventId || sharingTimelineEventId) return;
    setSharingTimelineEventId(eventId);
    try {
      const data = await shareRevisionTimelineRespuesta(id, eventId);
      const text = `Enlace a la ficha compartida: ${data.share_url} Contraseña: ${data.password}`;
      await navigator.clipboard.writeText(text);
      toast.success("Enlace de respuesta copiado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al compartir respuesta");
    } finally {
      setSharingTimelineEventId(null);
    }
  };

  const handleUpdateDraftBlock = (index, newBlock) => {
    setDraftBlocks((prev) => {
      const next = [...prev];
      next[index] = newBlock;
      return next;
    });
  };

  const handleRemoveDraftBlock = (index) => {
    setDraftBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDuplicateDraftBlock = (index) => {
    const block = draftBlocks[index];
    if (!block) return;
    const copy = { ...block, id: `blk_${Date.now()}_${Math.random().toString(36).slice(2, 9)}` };
    if (Array.isArray(block.imagenes)) copy.imagenes = block.imagenes.map((x) => ({ ...x }));
    if (Array.isArray(block.items)) copy.items = block.items.map((x) => ({ ...x }));
    setDraftBlocks((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const handleAddDraftBlockAfter = (afterIndex, tipo) => {
    setDraftBlocks((prev) => {
      const next = [...prev];
      const at = Math.min(Math.max(0, afterIndex + 1), next.length);
      next.splice(at, 0, createBlock(tipo));
      return next;
    });
  };

  const handleMoveDraftBlock = (fromIndex, toPosition) => {
    if (fromIndex === toPosition - 1) return;
    setDraftBlocks((prev) => {
      const removed = prev[fromIndex];
      const without = prev.filter((_, i) => i !== fromIndex);
      const insertAt = Math.min(Math.max(0, toPosition - 1), without.length);
      return [...without.slice(0, insertAt), removed, ...without.slice(insertAt)];
    });
  };

  const handleDragStart = (e) => {
    const idx = e.currentTarget.getAttribute("data-block-index");
    if (idx != null) setDragIndex(parseInt(idx, 10));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx ?? "");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
    const to = parseInt(e.currentTarget.getAttribute("data-block-index") || "", 10);
    setDragIndex(null);
    if (Number.isNaN(from) || Number.isNaN(to) || from === to) return;
    setDraftBlocks((prev) => {
      const next = [...prev];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      return next;
    });
  };

  const clientResponses = timeline.filter((t) => t.tipo === "respuesta_cliente");
  const activeSnapshot = snapshots.find((s) => s.id === activeSnapshotId) || snapshots[0] || null;
  const formatFecha = (iso) => {
    const d = new Date(iso || "");
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}h | ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className={isEmbeddedInRevisiones ? "min-h-dvh" : "min-h-dvh bg-background"}>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList className="rounded-md border border-input bg-background p-0.5 h-9">
              <TabsTrigger value="revisiones" className="rounded-[5px]">Revisiones</TabsTrigger>
              <TabsTrigger value="respuestas" className="rounded-[5px] flex items-center gap-2">
                Respuestas
                {(unreadByRevision[id] ?? 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs">
                    {unreadByRevision[id]}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            {activeTab === "revisiones" && (
              <div className="flex items-center gap-1 flex-wrap">
                <Tooltip><TooltipTrigger asChild><Button variant="secondary" size="icon" className="bg-background border border-input text-destructive" onClick={handleDelete}><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Eliminar</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="secondary" size="icon" className={modoVisualizar ? "bg-black text-white border border-black" : "bg-background border border-input"} onClick={() => setModoVisualizar(true)}><Eye className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Visualizar</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="secondary" size="icon" className={!modoVisualizar ? "bg-black text-white border border-black" : "bg-background border border-input"} onClick={() => setModoVisualizar(false)}><Pencil className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Editar</TooltipContent></Tooltip>
                <Tooltip><TooltipTrigger asChild><Button variant="secondary" size="icon" className="bg-background border border-input" onClick={handleShareRevision} disabled={sharingGeneric}><Share2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Compartir</TooltipContent></Tooltip>
                <Button variant="secondary" onClick={handleSave} disabled={saving} size="sm" className="h-9 gap-1.5 ml-1 bg-background border border-input">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar</Button>
              </div>
            )}
          </div>

          <TabsContent value="revisiones" className="mt-6">
            <div className="rounded-2xl border border-gray-300 bg-white px-4 sm:px-6 py-6">
              {modoVisualizar ? (
                <>
                  {(imagenCabecera || emoji || EMOJI_PREDETERMINADO) && (
                    <>
                      <div className="relative z-0 w-full h-[220px] rounded-lg border overflow-hidden bg-muted/30">
                        {imagenCabecera && <img src={imagenCabecera} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="relative z-10 -mt-10 mb-10 pl-4 w-fit">
                        <span className="text-7xl leading-none select-none inline-block" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{emoji || EMOJI_PREDETERMINADO}</span>
                      </div>
                    </>
                  )}
                  <h1 className="text-4xl font-semibold tracking-tight mb-8">{titulo || "Sin título"}</h1>
                  {draftBlocks.length > 0 && (
                    <div className="space-y-4">
                      {draftBlocks.map((b) => (
                        <div key={b.id}>{revisionBlockPreview(b)}</div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-8">
                  <div>
                    <div className="relative z-0 rounded-lg border overflow-hidden h-[220px] bg-muted/30">
                      {imagenCabecera ? (
                        <img src={imagenCabecera} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div
                          className="absolute inset-0 border border-dashed flex items-center justify-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/50 rounded-lg"
                          onClick={() => cabeceraInputRef.current?.click()}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-muted/50"); }}
                          onDragLeave={(e) => { e.currentTarget.classList.remove("bg-muted/50"); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove("bg-muted/50");
                            const file = (e.dataTransfer.files || [])[0];
                            if (file?.type?.startsWith("image/")) readFileAsDataUrl(file).then(setImagenCabecera).catch(() => {});
                          }}
                        >
                          Arrastra una imagen o haz clic
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 flex gap-2 z-30">
                        <Button type="button" variant="secondary" size="sm" onClick={() => fondoInputRef.current?.click()}>Fondo</Button>
                        <Button type="button" variant="secondary" size="sm" onClick={() => cabeceraInputRef.current?.click()}>Cambiar imagen</Button>
                        <Button type="button" variant="secondary" size="sm" onClick={() => { setImagenCabecera(""); setImagenFondo(""); }}>Quitar todo</Button>
                      </div>
                    </div>
                    <div className="relative z-10 -mt-10 mb-10 pl-4 w-fit pointer-events-none">
                      <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                        <PopoverTrigger asChild>
                          <span className="text-7xl leading-none select-none inline-block cursor-pointer pointer-events-auto" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                            {emoji || EMOJI_PREDETERMINADO}
                          </span>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 border-0" align="start">
                          <EmojiPicker onEmojiClick={(emojiData) => { setEmoji(emojiData.emoji); setEmojiPickerOpen(false); }} theme="light" />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <input ref={fondoInputRef} id="revision-fondo-file" type="file" accept="image/*" className="hidden" onChange={(e) => { const file = (e.target.files || [])[0]; if (file) readFileAsDataUrl(file).then(setImagenFondo).catch(() => {}); e.target.value = ""; }} />
                    <input ref={cabeceraInputRef} id="revision-cabecera-file" type="file" accept="image/*" className="hidden" onChange={(e) => { const file = (e.target.files || [])[0]; if (file) readFileAsDataUrl(file).then(setImagenCabecera).catch(() => {}); e.target.value = ""; }} />
                  </div>

                  <div className="space-y-2"><Label>Título</Label><Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título de la hoja" /></div>
                  <div className="space-y-2"><Label>Enlace público (slug)</Label><div className="flex items-center gap-2"><span className="text-sm text-muted-foreground shrink-0">/rv/</span><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="mi-revision" className="font-mono" /></div></div>

                  <div className="space-y-2">
                    <Label>¿Quién recibe notificación de email?</Label>
                    <div className="flex flex-wrap gap-2">
                      {usuariosPlataforma.map((u) => {
                        const email = (u.email || "").trim().toLowerCase();
                        if (!email) return null;
                        const selected = notificacionEmails.some((e) => (e || "").trim().toLowerCase() === email);
                        return (
                          <button key={email} type="button" onClick={() => setNotificacionEmails((prev) => (selected ? prev.filter((e) => (e || "").trim().toLowerCase() !== email) : [...prev, email]))} className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium ${selected ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background hover:bg-muted"}`}>
                            {(u.nombre || u.email || email).trim()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Card className="shadow-none border-gray-300">
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold">Nueva revisión</h3>
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="gap-2 h-9"><Plus className="w-4 h-4" />Añadir bloque</Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">{BLOCK_TYPES.map((opt) => <DropdownMenuItem key={opt.value} onSelect={() => setDraftBlocks((prev) => [...prev, createBlock(opt.value)])}>{opt.label}</DropdownMenuItem>)}</DropdownMenuContent>
                          </DropdownMenu>
                          <Button onClick={handleCreateSnapshot} disabled={creatingSnapshot} variant="outline" size="sm" className="gap-2 h-9">
                            {creatingSnapshot ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Añadir revisión
                          </Button>
                        </div>
                      </div>
                      {draftBlocks.length > 0 && (
                        <div className="space-y-2">
                          {draftBlocks.map((b, index) => (
                            <RevisionBlockRow
                              key={b.id}
                              block={b}
                              index={index}
                              allBlocks={draftBlocks}
                              onUpdate={handleUpdateDraftBlock}
                              onRemove={handleRemoveDraftBlock}
                              onDuplicate={handleDuplicateDraftBlock}
                              onMove={handleMoveDraftBlock}
                              onDragStart={handleDragStart}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              isDragging={dragIndex === index}
                              addWidgetMenuOptions={BLOCK_TYPES}
                              onAddWidgetAfter={(tipo) => handleAddDraftBlockAfter(index, tipo)}
                            />
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {snapshots.length > 0 && (
                  <Card className="shadow-none border-gray-300">
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-wrap gap-2">
                          {snapshots.map((s) => (
                            <Button
                              key={s.id}
                              variant={activeSnapshot?.id === s.id ? "default" : "outline"}
                              size="sm"
                              onClick={() => setActiveSnapshotId(s.id)}
                            >
                              Revisión {s.numero_revision}
                            </Button>
                          ))}
                        </div>
                        <div />
                      </div>
                      {activeSnapshot ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold">Revisión {activeSnapshot.numero_revision}</h3>
                          </div>
                          <div className="space-y-2">
                            {(activeSnapshot.bloques || []).map((b) => (
                              <div key={b.id} className="rounded-md border p-3 space-y-2">
                                <p className="text-xs text-muted-foreground">{b.tipo}</p>
                                {revisionBlockPreview(b) ?? (
                                  <p className="text-sm whitespace-pre-wrap">{linkifyText(b.texto || b.titulo || b.url || "")}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Aún no hay revisiones publicadas.</p>
                      )}
                    </CardContent>
                  </Card>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="respuestas" className="space-y-4">
            <p className="text-sm text-muted-foreground">Respuestas enviadas por el cliente desde el enlace público.</p>
            {!clientResponses.length ? (
              <p className="text-sm text-muted-foreground">No hay respuestas recibidas.</p>
            ) : (
              <div className="space-y-2">
                {clientResponses.map((item) => {
                  const isExpanded = expandedTimelineId === item.id;
                  const isUnread = id && !isRevisionTimelineEventRead(id, item.id);
                  return (
                    <Card key={item.id} className="shadow-none">
                      <button type="button" onClick={() => setExpandedTimelineId(isExpanded ? null : item.id)} className="w-full text-left py-3 px-4 flex items-center justify-between gap-2 hover:bg-muted/50 rounded-lg" aria-expanded={isExpanded}>
                        <span className="text-sm font-medium truncate min-w-0 flex items-center gap-2 flex-1">
                          {isUnread && <span className="inline-flex h-2 w-2 rounded-full bg-destructive shrink-0" aria-label="No leída" />}
                          <span className="truncate">Revisión {item.numero_revision || "-"}</span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs text-muted-foreground">{formatFecha(item.fecha_creacion)}</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                        </span>
                      </button>
                      {isExpanded && (
                        <CardContent className="py-0 pb-3 px-4 pt-0">
                          <div className="space-y-3 text-sm border-t pt-3">
                            {item.mensaje?.trim() ? <p className="whitespace-pre-wrap">{linkifyText(item.mensaje)}</p> : null}
                            <RevisionReplyAdjuntos adjuntos={item.adjuntos} />
                            {!item.mensaje?.trim() && (!item.adjuntos || !item.adjuntos.length) ? (
                              <p className="text-muted-foreground">Sin texto ni archivos.</p>
                            ) : null}
                            <div className="pt-2 border-t mt-2 flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                {isUnread && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRevisionTimelineEventRead(id, item.id);
                                    }}
                                  >
                                    Marcar como leída
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(e) => handleShareRevisionTimelineReply(e, item.id)}
                                  disabled={sharingTimelineEventId === item.id}
                                >
                                  <Share2 className="w-3 h-3" />
                                  {sharingTimelineEventId === item.id ? "Copiando..." : "Compartir ficha"}
                                </Button>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                                onClick={(e) => handleDeleteTimelineEvent(e, item.id)}
                                disabled={deletingTimelineEventId === item.id}
                                title="Eliminar respuesta"
                              >
                                <Trash2 className="w-3 h-3" />
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
